import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faDownload } from "@fortawesome/free-solid-svg-icons";

import "./InsuranceDocuments.scss";
import { DOWNLOAD_DOCUMENT_FILE } from "../../service/graphql/query";
import {
  base64ToBlob,
  displayDateStringFormat,
  retrieveFileType,
  splitName,
} from "../../service/utils/functions";
import { InsuranceDocumentEntry } from "../../service/utils/interfaces";
import { trackDownload } from "../../service/matomo";

const FileSaver = require("file-saver");

interface InsuranceDocumentsProps {
  data: InsuranceDocumentEntry[];
  loading?: boolean;
  darkMode: boolean;
}

const formatValidityDate = (value: string) => {
  return displayDateStringFormat(value);
};

const InsuranceDocuments = (props: InsuranceDocumentsProps) => {
  const [downloadDocFile] = useLazyQuery(DOWNLOAD_DOCUMENT_FILE);
  const [downloadingDocId, setDownloadingDocId] = useState("");

  const downloadHandler = async (doc: InsuranceDocumentEntry) => {
    if (!doc.fileName || !doc.source || downloadingDocId) return;
    const split = splitName(doc.fileName);
    const fileName = split.length > 0 ? split[0] : doc.fileName;
    let fileExtension = doc.fileType;
    if (!fileExtension || fileExtension.length <= 0)
      fileExtension = split.length > 0 ? split[1] : "pdf";

    setDownloadingDocId(doc.docId);
    try {
      const docFileRes = await downloadDocFile({
        variables: {
          fileId: doc.docId,
          source: doc.source,
        },
        context: { apiName: "middleware" },
      });
      if (!docFileRes.data?.downloadProtectedDocument) return;
      const fileType = retrieveFileType(fileExtension);
      const blob = base64ToBlob(
        docFileRes.data.downloadProtectedDocument,
        fileType
      );
      FileSaver.saveAs(blob, fileName + "." + fileExtension);
      trackDownload(fileName + "." + fileExtension, "insurance-document");
    } finally {
      setDownloadingDocId("");
    }
  };

  if (props.loading)
    return (
      <div className={"insurance-documents" + (props.darkMode ? " dark" : "")}>
        <p className="insurance-empty">Loading insurance documents...</p>
      </div>
    );

  if (!props.data || props.data.length === 0)
    return (
      <div className={"insurance-documents" + (props.darkMode ? " dark" : "")}>
        <p className="insurance-empty">
          There are no insurance documents available at the moment.
        </p>
      </div>
    );

  return (
    <div className={"insurance-documents" + (props.darkMode ? " dark" : "")}>
      {props.data.map((doc) => {
        const isDownloading = downloadingDocId === doc.docId;
        // Title comes from the document's category; the filename (with its
        // extension) is shown underneath it.
        const title =
          doc.documentCategory && doc.documentCategory.length > 0
            ? doc.documentCategory
            : doc.fileName;

        return (
          <div
            className="insurance-tile"
            key={"insurance-doc-" + doc.docId}
            onClick={() => downloadHandler(doc)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") downloadHandler(doc);
            }}
          >
            <FontAwesomeIcon icon={faFilePdf} className="file-icon" />

            <p className="tile-title" title={doc.fileName}>
              {title}
            </p>
            <p className="tile-subtitle">{doc.fileName}</p>

            <div className="tile-validity">
              <span className="validity-label">Period of cover</span>
              <span className="validity-value">
                {formatValidityDate(doc.periodFrom)}
                {doc.periodTo ? " – " + formatValidityDate(doc.periodTo) : ""}
              </span>
            </div>

            <div className={"tile-download" + (isDownloading ? " loading" : "")}>
              <FontAwesomeIcon icon={faDownload} />
              {isDownloading ? "Downloading..." : "Download"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InsuranceDocuments;
